import { ReactElement } from 'react';
import { SpeechBubble } from './speechBubbles/SpeechBubble';
import { ButtonGroup, ButtonSize, PrimaryButton } from '@ardoq/button';
import { Body, Content, DoqContainer, Footer } from './atoms';
import {
  DoqLayout,
  DoqType,
  DoqWithSpeechBubbleProps,
  SpeechBubbleProps,
} from './types';
import { PurpleSpeechBubble } from './speechBubbles/PurpleSpeechBubble';
import { ViridianSpeechBubble } from './speechBubbles/ViridianSpeechBubble';
import { DoqFace } from './DoqFace';
import { getDoqSizeForLayout } from './utils';
import { PirateGoldSpeechBubble } from './speechBubbles/PirateGoldSpeechBubble';
import { Green35SpeechBubble } from './speechBubbles/Green35SpeechBubble';
import { brand800SpeechBubble } from './speechBubbles/Brand800SpeechBubble';

type GetSpeechBubbleElement = (props: SpeechBubbleProps) => ReactElement;

const SPEECH_BUBBLE_MAP: Partial<Record<DoqType, GetSpeechBubbleElement>> = {
  [DoqType.BUBBLE_CHART_ERROR]: Green35SpeechBubble,
  [DoqType.CAPABILITY_MAP_ERROR]: PirateGoldSpeechBubble,
  [DoqType.COMPONENT_MATRIX_ERROR]: SpeechBubble,
  [DoqType.DEPENDENCY_MAP_ERROR]: brand800SpeechBubble,
  [DoqType.DEPENDENCY_MAP_GONE]: PurpleSpeechBubble,
  [DoqType.DEPENDENCY_WHEEL_ERROR]: PirateGoldSpeechBubble,
  [DoqType.PAGE_VIEW_ERROR]: brand800SpeechBubble,
  [DoqType.REFERENCE_TABLE_ERROR]: brand800SpeechBubble,
  [DoqType.RELATIONSHIPS_ERROR]: ViridianSpeechBubble,
  [DoqType.RELATIONSHIPS_GONE]: ViridianSpeechBubble,
  [DoqType.SWIMLANE_ERROR]: SpeechBubble,
  [DoqType.TABLE_ERROR]: PirateGoldSpeechBubble,
  [DoqType.TAGSCAPE_ERROR]: SpeechBubble,
  [DoqType.TIMELINE_ERROR]: SpeechBubble,
  [DoqType.TREEMAP_ERROR]: PirateGoldSpeechBubble,
};

const getDoqSpeechBubble = (doqType?: DoqType) => {
  if (!doqType) return SpeechBubble;

  return SPEECH_BUBBLE_MAP[doqType] || SpeechBubble;
};

type DoqSpeechBubbleProperties = { doqType?: DoqType } & SpeechBubbleProps;
const DoqSpeechBubble = ({ doqType, ...rest }: DoqSpeechBubbleProperties) => {
  const DoqSpeechBubbleComponent = getDoqSpeechBubble(doqType);
  return <DoqSpeechBubbleComponent {...rest} />;
};

const DoqWithSpeechBubble = ({
  doqType,
  title,
  layout,
  message,
  buttonLabel,
  onButtonClick,
  extraButton,
  buttonClickId,
}: DoqWithSpeechBubbleProps) => (
  <DoqContainer $layout={layout}>
    <DoqFace doqType={doqType} doqSize={getDoqSizeForLayout(layout)} />
    <Content $layout={layout}>
      <DoqSpeechBubble doqType={doqType} title={title} layout={layout} />
      <Body $layout={layout}>{message}</Body>
      <Footer $layout={layout}>
        {onButtonClick && (
          <ButtonGroup>
            {extraButton}
            <PrimaryButton
              buttonSize={
                layout === DoqLayout.DIALOG
                  ? ButtonSize.SMALL
                  : ButtonSize.REGULAR
              }
              data-click-id={buttonClickId}
              onClick={onButtonClick}
            >
              {buttonLabel}
            </PrimaryButton>
          </ButtonGroup>
        )}
      </Footer>
    </Content>
  </DoqContainer>
);

export { DoqWithSpeechBubble };
